import client from './client';
import type {
  Study,
  Site,
  Milestone,
  Issue,
  MonitoringVisit,
  EnrollmentDataPoint,
  CountryEnrollment,
  Country,
  Document,
  Vendor,
  BudgetItem,
  AuditLogEntry,
  ActivityItem,
  Alert,
  Notification,
  DashboardStats,
  StudyMetrics,
  PaginatedResponse,
  StudyFilters,
  IssueFilters,
  CreateStudyInput,
  CreateIssueInput,
  ScheduleVisitInput,
  MonitoringVisitDetail,
  TripReport,
  CreateTripReportInput,
  Finding,
  CreateFindingInput,
  ActionItem,
  CreateActionItemInput,
  FollowUpLetter,
  VisitStatus,
  KriDefinition,
  KriSnapshot,
  QualityToleranceLimit,
  QtlBreach,
  RiskSignal,
  RiskAssessment,
  ESignatureRecord,
  SignatureManifest,
  SafetyCase,
  RegulatoryReport,
  PaymentExportBatch,
  ReportConfigItem,
  WorkflowTransitions,
} from '@/types';

// ─── Helpers ────────────────────────────────────────────────────────────────

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    // The backend pagination contract uses `limit`; the frontend speaks
    // `pageSize`. Translate at the wire boundary so callers stay consistent.
    const key = k === 'pageSize' ? 'limit' : k;
    if (Array.isArray(v)) {
      v.forEach((item) => sp.append(key, String(item)));
    } else {
      sp.set(key, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Raw paginated envelope as returned by the backend. */
interface BackendPage<T> {
  data: T[];
  meta?: { page: number; limit: number; total: number; totalPages: number };
}

/**
 * Flatten the backend's `{ data, meta: { ..., limit } }` envelope into the flat
 * `PaginatedResponse` shape the UI consumes (`pageSize` instead of `limit`).
 */
function normalizePage<T>(raw: BackendPage<T>): PaginatedResponse<T> {
  const rows = raw?.data ?? [];
  const meta = raw?.meta;
  return {
    data: rows,
    page: meta?.page ?? 1,
    pageSize: meta?.limit ?? rows.length,
    total: meta?.total ?? rows.length,
    totalPages: meta?.totalPages ?? 1,
  };
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const { data } = await client.get<DashboardStats>('/dashboard/stats');
  return data;
}

export async function fetchAlerts(): Promise<Alert[]> {
  const { data } = await client.get<Alert[]>('/dashboard/alerts');
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function fetchNotifications(): Promise<Notification[]> {
  // The backend returns a paginated { data, meta } envelope and uses `isRead`.
  // Unwrap to a plain array and normalise to the frontend Notification shape so
  // callers can safely .filter()/.map() over it.
  const { data } = await client.get<unknown>('/notifications');
  const items: any[] = Array.isArray(data)
    ? (data as any[])
    : ((data as any)?.data ?? []);
  return items.map((n) => ({
    id: n.id,
    title: n.title,
    message: n.message,
    read: n.read ?? n.isRead ?? false,
    createdAt: n.createdAt,
    link: n.link,
  }));
}

export async function markNotificationRead(id: string): Promise<void> {
  await client.post(`/notifications/mark-read`, { notificationIds: [id] });
}

// ─── Studies ────────────────────────────────────────────────────────────────

export async function fetchStudies(
  filters?: StudyFilters,
  page = 1,
  pageSize = 25,
): Promise<PaginatedResponse<Study>> {
  const { data } = await client.get<BackendPage<Study>>(
    `/studies${qs({ ...filters, page, pageSize })}`,
  );
  return normalizePage(data);
}

export async function fetchStudy(studyId: string): Promise<Study> {
  const { data } = await client.get<Study>(`/studies/${studyId}`);
  return data;
}

export async function createStudy(input: CreateStudyInput): Promise<Study> {
  // Map the form shape to the backend CreateStudyDto (studyName/sponsorName +
  // enum phase). The shared Study has no therapeuticArea/indication/target — those
  // are persisted to the CTMS study-extension after the study is created.
  const phase = String(input.phase || '')
    .toUpperCase()
    .replace(/\s+/g, '_'); // "Phase III" -> "PHASE_III"
  const { data } = await client.post<Study>('/studies', {
    protocolNumber: input.protocolNumber,
    studyName: input.title,
    sponsorName: input.sponsor,
    phase,
    startDate: input.startDate || undefined,
    endDate: input.estimatedEndDate || undefined,
  });
  try {
    if (data?.id && (input.therapeuticArea || input.indication || input.targetEnrollment)) {
      await client.put(`/study-extensions/${data.id}`, {
        therapeuticArea: input.therapeuticArea || undefined,
        indication: input.indication || undefined,
        plannedSubjects: input.targetEnrollment || undefined,
      });
    }
  } catch {
    /* extension is best-effort; study creation already succeeded */
  }
  return data;
}

export async function updateStudy(
  studyId: string,
  input: Partial<Study>,
): Promise<Study> {
  const { data } = await client.put<Study>(`/studies/${studyId}`, input);
  return data;
}

/** Raw shape of the `/dashboard/study/:id` aggregate (nested, not flat). */
interface StudyDashboardResponse {
  enrollmentSummary?: { totalTarget?: number; totalActual?: number };
  siteStatusCounts?: Array<{ status: string; count: number }>;
  milestoneProgress?: {
    total?: number;
    completed?: number;
    overdue?: number;
    atRisk?: number;
  };
  issueStats?: { open?: number; overdue?: number };
  issueCountsBySeverity?: Array<{ severity: string; count: number }>;
}

export async function fetchStudyMetrics(studyId: string): Promise<StudyMetrics> {
  // Study-level metrics are served by the dashboard aggregate endpoint, which
  // returns a nested rollup — flatten it into the StudyMetrics the UI consumes.
  const { data } = await client.get<StudyDashboardResponse>(
    `/dashboard/study/${studyId}`,
  );

  const siteCounts = data?.siteStatusCounts ?? [];
  const milestones = data?.milestoneProgress ?? {};
  const milestoneTotal = milestones.total ?? 0;

  return {
    totalSites: siteCounts.reduce((sum, s) => sum + (s.count ?? 0), 0),
    activeSites: siteCounts
      .filter((s) => s.status?.toLowerCase() === 'active')
      .reduce((sum, s) => sum + (s.count ?? 0), 0),
    targetEnrollment: data?.enrollmentSummary?.totalTarget ?? 0,
    actualEnrollment: data?.enrollmentSummary?.totalActual ?? 0,
    openIssues:
      data?.issueStats?.open ??
      (data?.issueCountsBySeverity ?? []).reduce(
        (sum, i) => sum + (i.count ?? 0),
        0,
      ),
    overdueIssues: data?.issueStats?.overdue ?? 0,
    // "On track" = everything not flagged overdue or at-risk.
    milestonesOnTrack: Math.max(
      0,
      milestoneTotal - (milestones.overdue ?? 0) - (milestones.atRisk ?? 0),
    ),
    milestonesOverdue: milestones.overdue ?? 0,
  };
}

/** Operational rollups (recruitment / supply / resourcing / closeout / predictions). */
export interface StudyOperations {
  recruitment: {
    total: number;
    byStatus: Array<{ status: string; count: number }>;
    enrolled: number;
  };
  supply: {
    lots: number;
    availableUnits: number;
    lowStockLots: number;
    pendingShipments: number;
  };
  resourcing: { activeAssignments: number; distinctCras: number };
  closeout: {
    byStatus: Array<{ status: string; count: number }>;
    complete: number;
    inProgress: number;
  };
  predictions: number;
}

export async function fetchStudyOperations(
  studyId: string,
): Promise<StudyOperations> {
  const { data } = await client.get<StudyOperations>(
    `/dashboard/operations/${studyId}`,
  );
  return data;
}

export async function fetchStudyActivity(studyId: string): Promise<ActivityItem[]> {
  // The study activity feed is backed by the study-scoped audit log.
  const { data } = await client.get<unknown>(`/audit/study/${studyId}`);
  return ((data as any)?.data ?? data) as ActivityItem[];
}

// ─── Countries ──────────────────────────────────────────────────────────────

export async function fetchCountries(studyId: string): Promise<Country[]> {
  const { data } = await client.get<unknown>(
    `/site-management/countries${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as Country[];
}

// ─── Sites ──────────────────────────────────────────────────────────────────

export async function fetchSites(
  studyId: string,
  page = 1,
  pageSize = 25,
): Promise<PaginatedResponse<Site>> {
  const { data } = await client.get<BackendPage<Site>>(
    `/site-management/sites${qs({ studyId, page, pageSize })}`,
  );
  return normalizePage(data);
}

export async function fetchSite(_studyId: string, siteId: string): Promise<Site> {
  const { data } = await client.get<Site>(`/site-management/sites/${siteId}`);
  return data;
}

// ─── Site Management write-UIs (create/update/status + activation) ────────────

/**
 * Create a site. Matches backend `CreateSiteDto`: requires { studyId, siteNumber,
 * siteName }; optional countryId/country/city/state/address/timezone/piName/
 * piEmail/phoneNumber/faxNumber/enrollmentTarget. POST `/site-management/sites`.
 */
export async function createSite(body: Record<string, unknown>): Promise<Site> {
  const { data } = await client.post<Site>('/site-management/sites', body);
  return data;
}

/**
 * Update a site. Matches backend `UpdateSiteDto` (all fields optional):
 * siteName/countryId/country/city/state/address/timezone/piName/piEmail/
 * phoneNumber/faxNumber/enrollmentTarget/status. PUT `/site-management/sites/:id`.
 */
export async function updateSite(
  siteId: string,
  body: Record<string, unknown>,
): Promise<Site> {
  const { data } = await client.put<Site>(
    `/site-management/sites/${siteId}`,
    body,
  );
  return data;
}

/**
 * Change a site's status through the allowed transitions. Matches backend
 * `ChangeSiteStatusDto`: { status, reason? } where status is one of the shared
 * SiteStatus enum values. POST `/site-management/sites/:id/status`.
 */
export async function changeSiteStatus(
  siteId: string,
  body: { status: string; reason?: string },
): Promise<Site> {
  const { data } = await client.post<Site>(
    `/site-management/sites/${siteId}/status`,
    body,
  );
  return data;
}

/**
 * Create an investigator. Matches backend `CreateInvestigatorDto`: requires
 * { firstName, lastName, email }; optional phone/title/specialty/institution/
 * medicalLicenseNo/licenseExpiry/cvDocumentUrl/notes. POST
 * `/site-management/investigators`.
 */
export async function createInvestigator(
  body: Record<string, unknown>,
): Promise<CtmsInvestigatorRow> {
  const { data } = await client.post<CtmsInvestigatorRow>(
    '/site-management/investigators',
    body,
  );
  return data;
}

// ─── Activation checklists & tasks ────────────────────────────────────────────

/** A single activation task as returned with a checklist. */
export interface ActivationTaskRow {
  id: string;
  checklistId: string;
  taskName: string;
  taskCategory?: string | null;
  isRequired?: boolean;
  status: string;
  sortOrder?: number;
  dueDate?: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
  notes?: string | null;
}

/** Per-checklist progress rollup returned by `getChecklistProgress`. */
export interface ActivationChecklistProgress {
  id: string;
  siteId: string;
  studyId: string;
  checklistName: string;
  templateName?: string | null;
  isComplete?: boolean;
  totalTasks?: number;
  tasks: ActivationTaskRow[];
  progress: {
    total: number;
    completed: number;
    blocked: number;
    overdue: number;
    percentComplete: number;
  };
}

/**
 * Create an activation checklist for a site. Matches backend
 * `CreateActivationChecklistDto`: { siteId, studyId, checklistName,
 * templateName?, tasks: [{ taskName, taskCategory?, isRequired?, sortOrder?,
 * dueDate? }] }. POST `/site-management/activation-checklists`.
 */
export async function createActivationChecklist(
  body: {
    siteId: string;
    studyId: string;
    checklistName: string;
    templateName?: string;
    tasks: Array<{
      taskName: string;
      taskCategory?: string;
      isRequired?: boolean;
      sortOrder?: number;
      dueDate?: string;
    }>;
  },
): Promise<ActivationChecklistProgress> {
  const { data } = await client.post<ActivationChecklistProgress>(
    '/site-management/activation-checklists',
    body,
  );
  return data;
}

/**
 * Update an activation task's status. Matches backend `UpdateActivationTaskDto`:
 * { status, completedBy?, notes? } where status is one of NOT_STARTED /
 * IN_PROGRESS / COMPLETED / BLOCKED / WAIVED / NOT_APPLICABLE. PUT
 * `/site-management/activation-tasks/:id`.
 */
export async function updateActivationTask(
  id: string,
  body: { status: string; completedBy?: string; notes?: string },
): Promise<ActivationTaskRow> {
  const { data } = await client.put<ActivationTaskRow>(
    `/site-management/activation-tasks/${id}`,
    body,
  );
  return data;
}

/**
 * Fetch activation progress for a site. The backend returns an ARRAY of
 * checklists (each with a `progress` rollup), NOT an envelope. GET
 * `/site-management/sites/:id/activation-progress`.
 */
export async function fetchActivationProgress(
  siteId: string,
): Promise<ActivationChecklistProgress[]> {
  const { data } = await client.get<unknown>(
    `/site-management/sites/${siteId}/activation-progress`,
  );
  return ((data as any)?.data ?? data ?? []) as ActivationChecklistProgress[];
}

/**
 * Complete (sign off) an activation checklist. The backend 400s if any required
 * task is still incomplete — surface that error to the caller. POST
 * `/site-management/activation-checklists/:id/complete`.
 */
export async function completeActivationChecklist(
  id: string,
): Promise<ActivationChecklistProgress> {
  const { data } = await client.post<ActivationChecklistProgress>(
    `/site-management/activation-checklists/${id}/complete`,
  );
  return data;
}

// ─── Milestones ─────────────────────────────────────────────────────────────

export async function fetchMilestones(studyId: string): Promise<Milestone[]> {
  const { data } = await client.get<Milestone[]>(`/studies/${studyId}/milestones`);
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function updateMilestone(
  studyId: string,
  milestoneId: string,
  input: Partial<Milestone>,
): Promise<Milestone> {
  const { data } = await client.put<Milestone>(
    `/studies/${studyId}/milestones/${milestoneId}`,
    input,
  );
  return data;
}

export async function reorderMilestones(
  studyId: string,
  orderedIds: string[],
): Promise<void> {
  // TODO: backend milestone-reorder endpoint. Until it exists, persist the new
  // order via per-milestone PUTs (sortOrder follows array position).
  await Promise.all(
    orderedIds.map((id, index) =>
      updateMilestone(studyId, id, { sortOrder: index }),
    ),
  );
}

// ─── Enrollment ─────────────────────────────────────────────────────────────

export async function fetchEnrollmentCurve(
  studyId: string,
): Promise<EnrollmentDataPoint[]> {
  const { data } = await client.get<any>(`/dashboard/enrollment/${studyId}`);
  // The dashboard enrollment endpoint returns an aggregate object; adapt to the
  // curve shape when a `curve`/`timeline` series is present.
  const series = data?.curve ?? data?.timeline ?? data?.points;
  if (Array.isArray(series)) return series as EnrollmentDataPoint[];
  // TODO: backend enrollment-curve endpoint
  return [];
}

export async function fetchCountryEnrollment(
  studyId: string,
): Promise<CountryEnrollment[]> {
  const { data } = await client.get<any>(`/dashboard/enrollment/${studyId}`);
  const byCountry = data?.byCountry ?? data?.countries;
  if (Array.isArray(byCountry)) return byCountry as CountryEnrollment[];
  // TODO: backend enrollment-curve endpoint
  return [];
}

// ─── Monitoring ─────────────────────────────────────────────────────────────

export async function fetchMonitoringVisits(
  studyId: string,
): Promise<MonitoringVisit[]> {
  const { data } = await client.get<unknown>(
    `/monitoring-visits${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as MonitoringVisit[];
}

export async function fetchVisit(
  _studyId: string,
  visitId: string,
): Promise<MonitoringVisit> {
  const { data } = await client.get<MonitoringVisit>(
    `/monitoring-visits/${visitId}`,
  );
  return data;
}

export async function scheduleVisit(
  input: ScheduleVisitInput,
): Promise<MonitoringVisit> {
  const { data } = await client.post<MonitoringVisit>(
    `/monitoring-visits`,
    input,
  );
  return data;
}

export async function updateVisit(
  _studyId: string,
  visitId: string,
  input: Partial<MonitoringVisit>,
): Promise<MonitoringVisit> {
  const { data } = await client.put<MonitoringVisit>(
    `/monitoring-visits/${visitId}`,
    input,
  );
  return data;
}

// ─── Issues / Deviations / CAPAs ────────────────────────────────────────────

export async function fetchIssues(
  studyId: string,
  filters?: IssueFilters,
  page = 1,
  pageSize = 25,
): Promise<PaginatedResponse<Issue>> {
  const { data } = await client.get<BackendPage<Issue>>(
    `/issues-quality/issues${qs({ studyId, ...filters, page, pageSize })}`,
  );
  return normalizePage(data);
}

export async function createIssue(input: CreateIssueInput): Promise<Issue> {
  const { data } = await client.post<Issue>(
    `/issues-quality/issues`,
    input,
  );
  return data;
}

export async function updateIssue(
  _studyId: string,
  issueId: string,
  input: Partial<Issue>,
): Promise<Issue> {
  const { data } = await client.put<Issue>(
    `/issues-quality/issues/${issueId}`,
    input,
  );
  return data;
}

// ─── Financials ─────────────────────────────────────────────────────────────

export async function fetchBudgetItems(studyId: string): Promise<BudgetItem[]> {
  const { data } = await client.get<unknown>(
    `/financials/budgets${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as BudgetItem[];
}

export async function createBudget(body: Record<string, unknown>): Promise<any> {
  const { data } = await client.post('/financials/budgets', body);
  return data;
}

export async function fetchContracts(studyId: string): Promise<any[]> {
  const { data } = await client.get<unknown>(
    `/financials/contracts${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as any[];
}

export async function fetchGrants(studyId: string): Promise<any[]> {
  const { data } = await client.get<unknown>(
    `/financials/grants${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as any[];
}

export async function fetchPaymentEvents(studyId: string): Promise<any[]> {
  const { data } = await client.get<unknown>(
    `/financials/payment-events${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as any[];
}

// ─── Vendors ────────────────────────────────────────────────────────────────

export async function fetchVendors(studyId: string): Promise<Vendor[]> {
  const { data } = await client.get<unknown>(`/vendors${qs({ studyId })}`);
  return ((data as any)?.data ?? data) as Vendor[];
}

export async function createVendor(body: Record<string, unknown>): Promise<any> {
  const { data } = await client.post('/vendors', body);
  return data;
}

// ─── Documents ──────────────────────────────────────────────────────────────

export async function fetchDocuments(studyId: string): Promise<Document[]> {
  const { data } = await client.get<unknown>(`/documents${qs({ studyId })}`);
  return ((data as any)?.data ?? data) as Document[];
}

// ─── Investigators ──────────────────────────────────────────────────────────

/**
 * Backend CtmsInvestigator row as returned by the list endpoint. The shared
 * `Investigator` type predates the merged schema, so surface the real columns
 * (firstName/lastName, title, isActive, …) the UI actually renders.
 */
export interface CtmsInvestigatorRow {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string | null;
  title?: string | null;
  specialty?: string | null;
  institution?: string | null;
  medicalLicenseNo?: string | null;
  licenseExpiry?: string | null;
  isActive?: boolean;
  status?: string;
  createdAt?: string;
}

export async function fetchInvestigators(
  page = 1,
  pageSize = 100,
): Promise<PaginatedResponse<CtmsInvestigatorRow>> {
  const { data } = await client.get<BackendPage<CtmsInvestigatorRow>>(
    `/site-management/investigators${qs({ page, pageSize })}`,
  );
  return normalizePage(data);
}

/** A single delegation-log row (site-scoped on the backend). */
export interface DelegationLogRow {
  id: string;
  siteId: string;
  studyId: string;
  delegatedTask?: string;
  status?: string;
  delegatedByName?: string;
  startDate?: string;
  endDate?: string | null;
  staff?: { firstName?: string; lastName?: string; name?: string } | null;
}

export async function fetchDelegationLogs(params?: {
  siteId?: string;
  studyId?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<DelegationLogRow>> {
  const { data } = await client.get<BackendPage<DelegationLogRow>>(
    `/site-management/delegation-logs${qs({ pageSize: 100, ...params })}`,
  );
  return normalizePage(data);
}

/** A single training-record row (site-scoped on the backend). */
export interface TrainingRecordRow {
  id: string;
  siteId: string;
  studyId: string;
  trainingName?: string;
  trainingType?: string;
  status?: string;
  isRequired?: boolean;
  assignedDate?: string;
  completedDate?: string | null;
  expiryDate?: string | null;
  staff?: { firstName?: string; lastName?: string; name?: string } | null;
}

export async function fetchTrainingRecords(params?: {
  siteId?: string;
  studyId?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<TrainingRecordRow>> {
  const { data } = await client.get<BackendPage<TrainingRecordRow>>(
    `/site-management/training-records${qs({ pageSize: 100, ...params })}`,
  );
  return normalizePage(data);
}

// ─── Monitoring (extended) ──────────────────────────────────────────────────

export async function fetchVisitDetail(
  _studyId: string,
  visitId: string,
): Promise<MonitoringVisitDetail> {
  const { data } = await client.get<MonitoringVisitDetail>(
    `/monitoring-visits/${visitId}`,
  );
  return data;
}

export async function updateVisitStatus(
  _studyId: string,
  visitId: string,
  status: VisitStatus,
): Promise<MonitoringVisit> {
  const { data } = await client.post<MonitoringVisit>(
    `/monitoring-visits/${visitId}/status`,
    { status },
  );
  return data;
}

// ─── Trip Reports ──────────────────────────────────────────────────────────

export async function fetchTripReport(
  _studyId: string,
  // NOTE: backend keys trip-reports by their own id (`/trip-reports/:id`); the
  // caller passes the trip-report id in this position.
  tripReportId: string,
): Promise<TripReport> {
  const { data } = await client.get<TripReport>(
    `/trip-reports/${tripReportId}`,
  );
  return data;
}

export async function createTripReport(
  _studyId: string,
  input: CreateTripReportInput,
): Promise<TripReport> {
  const { data } = await client.post<TripReport>(`/trip-reports`, input);
  return data;
}

export async function updateTripReport(
  _studyId: string,
  tripReportId: string,
  input: Partial<TripReport>,
): Promise<TripReport> {
  const { data } = await client.put<TripReport>(
    `/trip-reports/${tripReportId}`,
    input,
  );
  return data;
}

export async function approveTripReport(
  _studyId: string,
  tripReportId: string,
): Promise<TripReport> {
  const { data } = await client.post<TripReport>(
    `/trip-reports/${tripReportId}/approve`,
  );
  return data;
}

export async function rejectTripReport(
  _studyId: string,
  tripReportId: string,
  reason: string,
): Promise<TripReport> {
  // Backend uses `request-revision` for sending a report back to the author.
  const { data } = await client.post<TripReport>(
    `/trip-reports/${tripReportId}/request-revision`,
    { reason },
  );
  return data;
}

// ─── Findings ──────────────────────────────────────────────────────────────

export async function fetchFindings(
  _studyId: string,
  visitId: string,
): Promise<Finding[]> {
  const { data } = await client.get<unknown>(
    `/monitoring-visits/${visitId}/findings`,
  );
  return ((data as any)?.data ?? data) as Finding[];
}

export async function createFinding(
  _studyId: string,
  visitId: string,
  input: CreateFindingInput,
): Promise<Finding> {
  const { data } = await client.post<Finding>(`/monitoring-findings`, {
    ...input,
    visitId,
  });
  return data;
}

// ─── Action Items ──────────────────────────────────────────────────────────

export async function fetchActionItems(studyId: string): Promise<ActionItem[]> {
  const { data } = await client.get<unknown>(
    `/action-items${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as ActionItem[];
}

export async function createActionItem(
  studyId: string,
  visitId: string,
  input: CreateActionItemInput,
): Promise<ActionItem> {
  const { data } = await client.post<ActionItem>(`/action-items`, {
    ...input,
    studyId,
    visitId,
  });
  return data;
}

export async function updateActionItem(
  _studyId: string,
  actionItemId: string,
  input: Partial<ActionItem>,
): Promise<ActionItem> {
  const { data } = await client.put<ActionItem>(
    `/action-items/${actionItemId}`,
    input,
  );
  return data;
}

export async function bulkUpdateActionItems(
  studyId: string,
  ids: string[],
  updates: Partial<ActionItem>,
): Promise<void> {
  // Backend has no bulk endpoint; fan out to per-item PUTs.
  await Promise.all(ids.map((id) => updateActionItem(studyId, id, updates)));
}

// ─── Follow-up Letters ─────────────────────────────────────────────────────

/**
 * Create a follow-up letter. Backend `CreateFollowUpLetterDto` requires
 * `tripReportId` + `studyId` (NOT a visitId), plus optional letterContent /
 * recipientName / recipientEmail. POST `/follow-up-letters`.
 */
export interface CreateFollowUpLetterBody {
  tripReportId: string;
  studyId: string;
  letterContent?: string;
  recipientName?: string;
  recipientEmail?: string;
}

export async function createFollowUpLetter(
  body: CreateFollowUpLetterBody,
): Promise<FollowUpLetter> {
  const { data } = await client.post<FollowUpLetter>(
    `/follow-up-letters`,
    body,
  );
  return data;
}

export async function sendFollowUpLetter(
  // Backend keys follow-up letters by their own id (`/follow-up-letters/:id`).
  letterId: string,
): Promise<FollowUpLetter> {
  const { data } = await client.post<FollowUpLetter>(
    `/follow-up-letters/${letterId}/send`,
  );
  return data;
}

export async function acknowledgeFollowUpLetter(
  letterId: string,
  body?: Record<string, unknown>,
): Promise<FollowUpLetter> {
  const { data } = await client.post<FollowUpLetter>(
    `/follow-up-letters/${letterId}/acknowledge`,
    body,
  );
  return data;
}

/** Close a follow-up letter. POST `/follow-up-letters/:id/close`. */
export async function closeFollowUpLetter(
  letterId: string,
): Promise<FollowUpLetter> {
  const { data } = await client.post<FollowUpLetter>(
    `/follow-up-letters/${letterId}/close`,
  );
  return data;
}

// ─── Study Sites (alias used by SiteListPage) ─────────────────────────────

export async function fetchStudySites(
  studyId: string,
  page = 1,
  pageSize = 25,
): Promise<PaginatedResponse<Site>> {
  const { data } = await client.get<BackendPage<Site>>(
    `/site-management/sites${qs({ studyId, page, pageSize })}`,
  );
  return normalizePage(data);
}

// ─── Feasibility ───────────────────────────────────────────────────────────

export async function fetchFeasibilitySurveys(studyId: string): Promise<any[]> {
  const { data } = await client.get<unknown>(
    `/site-management/feasibility-surveys${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as any[];
}

export async function fetchSurveyResponses(surveyId: string): Promise<any[]> {
  const { data } = await client.get<unknown>(
    `/site-management/feasibility-responses${qs({ surveyId })}`,
  );
  return ((data as any)?.data ?? data) as any[];
}

export async function fetchFeasibilityScores(studyId: string): Promise<any[]> {
  // Scores live on the feasibility-response records; surface them via the
  // responses list filtered by study.
  const { data } = await client.get<unknown>(
    `/site-management/feasibility-responses${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as any[];
}

export async function createFeasibilitySurvey(
  studyId: string,
  input: { title: string; description: string },
): Promise<any> {
  const { data } = await client.post(
    `/site-management/feasibility-surveys`,
    { ...input, studyId },
  );
  return data;
}

// ─── Audit Log ──────────────────────────────────────────────────────────────

export interface AuditLogFilters {
  page?: number;
  pageSize?: number;
  action?: string;
  entityType?: string;
  entityId?: string;
  studyId?: string;
  siteId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function fetchAuditLog(
  params: AuditLogFilters = {},
): Promise<PaginatedResponse<AuditLogEntry>> {
  const { page = 1, pageSize = 50, ...rest } = params;
  const { data } = await client.get<BackendPage<AuditLogEntry>>(
    `/audit/logs${qs({ page, pageSize, ...rest })}`,
  );
  return normalizePage(data);
}

// ─── RBQM: KRI Definitions ───────────────────────────────────────────────────

export async function fetchKriDefinitions(
  studyId: string,
): Promise<KriDefinition[]> {
  // Backend `listKriDefinitions` returns a PaginatedResponse `{ data, meta }`.
  const { data } = await client.get<KriDefinition[]>(
    `/kri-definitions${qs({ studyId })}`,
  );
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function createKriDefinition(
  body: Partial<KriDefinition>,
): Promise<KriDefinition> {
  const { data } = await client.post<KriDefinition>('/kri-definitions', body);
  return data;
}

export async function updateKriDefinition(
  id: string,
  body: Partial<KriDefinition>,
): Promise<KriDefinition> {
  const { data } = await client.put<KriDefinition>(
    `/kri-definitions/${id}`,
    body,
  );
  return data;
}

export async function deleteKriDefinition(id: string): Promise<void> {
  await client.delete(`/kri-definitions/${id}`);
}

export async function evaluateKri(
  id: string,
  siteValues: unknown,
): Promise<KriSnapshot> {
  const { data } = await client.post<KriSnapshot>(
    `/kri-definitions/${id}/evaluate`,
    { siteValues },
  );
  return data;
}

export async function fetchKriSnapshots(id: string): Promise<KriSnapshot[]> {
  const { data } = await client.get<KriSnapshot[]>(
    `/kri-definitions/${id}/snapshots`,
  );
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

// ─── RBQM: EDC-sourced KRI engine + Central Statistical Monitoring ────────────

export interface KriMetric {
  key: string;
  label: string;
  description?: string;
  category?: string;
  domain?: string;
  metricType?: string;
  defaultDirection?: string;
  unit?: string;
  defaultWarning?: number;
  defaultCritical?: number;
  numeratorLabel?: string;
  denominatorLabel?: string;
}

/** Catalog of EDC metrics a KRI can be bound to. */
export async function fetchKriMetricCatalog(): Promise<KriMetric[]> {
  const { data } = await client.get<unknown>(`/kri-metric-catalog`);
  return ((data as any)?.data ?? data ?? []) as KriMetric[];
}

export interface KriBindingBody {
  sourceType: string;
  periodDays?: number;
  applyDefaults?: boolean;
}

/** Bind a KRI definition to an EDC metric source. */
export async function setKriBinding(
  id: string,
  body: KriBindingBody,
): Promise<KriDefinition> {
  const { data } = await client.post<KriDefinition>(
    `/kri-definitions/${id}/binding`,
    body,
  );
  return data;
}

export interface KriComputeBreaching {
  siteId: string;
  value?: number;
  level?: string;
  reason?: string;
}

export interface KriComputeSnapshot {
  studyMean?: number;
  studyMedian?: number;
  studyStdDev?: number;
  sitesAtWarning?: number;
  sitesAtCritical?: number;
  signalsGenerated?: number;
  siteCount?: number;
  breaching?: KriComputeBreaching[];
}

/** Compute a single KRI from EDC data. */
export async function computeKriFromEdc(
  id: string,
): Promise<KriComputeSnapshot> {
  const { data } = await client.post<KriComputeSnapshot>(
    `/kri-definitions/${id}/compute`,
  );
  return data;
}

export interface ComputeAllKriResult {
  kriDefinitionId: string;
  name: string;
  computed: boolean;
  reason?: string;
  sitesAtWarning?: number;
  sitesAtCritical?: number;
  signalsGenerated?: number;
}

export interface ComputeAllKrisResponse {
  total: number;
  computed: number;
  skipped: number;
  signalsGenerated: number;
  results: ComputeAllKriResult[];
}

/** Compute all EDC-bound KRIs for a study. */
export async function computeAllKris(
  studyId: string,
): Promise<ComputeAllKrisResponse> {
  const { data } = await client.post<ComputeAllKrisResponse>(
    `/kri-definitions/compute-all`,
    { studyId },
  );
  return data;
}

export interface CsmContributor {
  kri: string;
  value?: number;
  z?: number;
  level?: string;
}

export interface CsmSite {
  siteId: string;
  composite: number;
  extremeFlags: number;
  riskBand: 'HIGH' | 'ELEVATED' | 'NORMAL';
  contributors: CsmContributor[];
}

export interface CsmResult {
  analyzed: boolean;
  reason?: string;
  kriCount: number;
  siteCount: number;
  cutoff?: string;
  signalsGenerated: number;
  sites: CsmSite[];
}

/** Run Central Statistical Monitoring outlier detection. */
export async function detectCsmOutliers(
  studyId: string,
  raiseSignals?: boolean,
): Promise<CsmResult> {
  const { data } = await client.post<CsmResult>(`/csm/detect`, {
    studyId,
    raiseSignals,
  });
  return data;
}

export interface KriSeedProfile {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  metricCount?: number;
}

/** List available KRI seed profiles (e.g. ICH E6 templates). */
export async function fetchKriSeedProfiles(): Promise<KriSeedProfile[]> {
  const { data } = await client.get<unknown>(`/kri-seed-profiles`);
  return ((data as any)?.data ?? data ?? []) as KriSeedProfile[];
}

/** Apply a seed profile to a study (creates KRI definitions). */
export async function applyKriSeedProfile(
  profileId: string,
  studyId: string,
): Promise<{ created: number; skipped: number }> {
  const { data } = await client.post<{ created: number; skipped: number }>(
    `/kri-seed-profiles/${profileId}/apply`,
    { studyId },
  );
  return data;
}

// ─── RBQM: Quality Tolerance Limits ──────────────────────────────────────────

export async function fetchQtls(
  studyId: string,
): Promise<QualityToleranceLimit[]> {
  // Backend `listQtls` returns a PaginatedResponse `{ data, meta }`.
  const { data } = await client.get<QualityToleranceLimit[]>(
    `/qtls${qs({ studyId })}`,
  );
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function createQtl(
  body: Partial<QualityToleranceLimit>,
): Promise<QualityToleranceLimit> {
  const { data } = await client.post<QualityToleranceLimit>('/qtls', body);
  return data;
}

export async function recordQtlBreach(
  qtlId: string,
  body: Partial<QtlBreach>,
): Promise<QtlBreach> {
  const { data } = await client.post<QtlBreach>(
    `/qtls/${qtlId}/breaches`,
    body,
  );
  return data;
}

export async function fetchQtlBreaches(qtlId: string): Promise<QtlBreach[]> {
  const { data } = await client.get<QtlBreach[]>(`/qtls/${qtlId}/breaches`);
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

// ─── RBQM: Risk Signals ──────────────────────────────────────────────────────

export async function fetchRiskSignals(
  studyId: string,
  status?: string,
): Promise<RiskSignal[]> {
  // Backend `listRiskSignals` returns a PaginatedResponse `{ data, meta }`.
  const { data } = await client.get<RiskSignal[]>(
    `/risk-signals${qs({ studyId, status })}`,
  );
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function createRiskSignal(
  body: Partial<RiskSignal>,
): Promise<RiskSignal> {
  const { data } = await client.post<RiskSignal>('/risk-signals', body);
  return data;
}

export async function updateRiskSignal(
  id: string,
  body: Partial<RiskSignal>,
): Promise<RiskSignal> {
  const { data } = await client.put<RiskSignal>(`/risk-signals/${id}`, body);
  return data;
}

// ─── RBQM: Risk Assessments ──────────────────────────────────────────────────

export async function fetchRiskAssessments(
  studyId: string,
): Promise<RiskAssessment[]> {
  // Backend `listRiskAssessments` returns a PaginatedResponse `{ data, meta }`.
  const { data } = await client.get<RiskAssessment[]>(
    `/risk-assessments${qs({ studyId })}`,
  );
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function createRiskAssessment(
  body: Partial<RiskAssessment>,
): Promise<RiskAssessment> {
  const { data } = await client.post<RiskAssessment>('/risk-assessments', body);
  return data;
}

// ─── RBQM: Risk Register (categories & items) ────────────────────────────────

export interface RiskCategory {
  id: string;
  assessmentId: string;
  name: string;
  description?: string;
}

export interface RiskItem {
  id: string;
  assessmentId: string;
  categoryId?: string;
  title: string;
  description?: string;
  impactScore: number;
  probabilityScore: number;
  detectabilityScore: number;
  riskPriorityNumber: number;
  inherentRiskLevel: string;
  createdAt?: string;
}

export async function fetchRiskCategories(
  assessmentId: string,
): Promise<RiskCategory[]> {
  const { data } = await client.get<unknown>(
    `/risk-assessments/${assessmentId}/categories`,
  );
  return ((data as any)?.data ?? data) as RiskCategory[];
}

export async function createRiskCategory(
  assessmentId: string,
  body: Partial<RiskCategory>,
): Promise<RiskCategory> {
  const { data } = await client.post<RiskCategory>(
    `/risk-assessments/${assessmentId}/categories`,
    body,
  );
  return data;
}

export async function fetchRiskItems(
  assessmentId: string,
): Promise<RiskItem[]> {
  const { data } = await client.get<unknown>(
    `/risk-assessments/${assessmentId}/items`,
  );
  return ((data as any)?.data ?? data) as RiskItem[];
}

export async function createRiskItem(
  assessmentId: string,
  body: {
    title: string;
    description?: string;
    categoryId?: string;
    impactScore: number;
    probabilityScore: number;
    detectabilityScore: number;
  },
): Promise<RiskItem> {
  const { data } = await client.post<RiskItem>(
    `/risk-assessments/${assessmentId}/items`,
    body,
  );
  return data;
}

// ─── RBQM: Signal Actions ────────────────────────────────────────────────────

export interface SignalAction {
  id: string;
  signalId: string;
  actionType?: string;
  description: string;
  performedBy?: string;
  createdAt?: string;
}

export async function fetchSignalActions(
  signalId: string,
): Promise<SignalAction[]> {
  const { data } = await client.get<unknown>(
    `/risk-signals/${signalId}/actions`,
  );
  return ((data as any)?.data ?? data) as SignalAction[];
}

export async function createSignalAction(
  signalId: string,
  body: Partial<SignalAction>,
): Promise<SignalAction> {
  const { data } = await client.post<SignalAction>(
    `/risk-signals/${signalId}/actions`,
    body,
  );
  return data;
}

// ─── RBQM: Monitoring Recommendations ────────────────────────────────────────

export interface MonitoringRecommendation {
  id: string;
  studyId: string;
  siteId?: string;
  siteName?: string;
  riskScore: number;
  recommendationType: string;
  dataReviewScore?: number;
  enrollmentScore?: number;
  qualityScore?: number;
  safetyScore?: number;
  rationale?: string;
  createdAt?: string;
}

export async function computeMonitoringRecommendations(
  studyId: string,
): Promise<MonitoringRecommendation[]> {
  const { data } = await client.post<unknown>(
    `/monitoring-recommendations/compute`,
    { studyId },
  );
  return ((data as any)?.data ?? data) as MonitoringRecommendation[];
}

export async function fetchMonitoringRecommendations(
  studyId: string,
): Promise<MonitoringRecommendation[]> {
  const { data } = await client.get<unknown>(
    `/monitoring-recommendations${qs({ studyId })}`,
  );
  return ((data as any)?.data ?? data) as MonitoringRecommendation[];
}

// ─── RBQM: Risk Monitoring Plan ──────────────────────────────────────────────

export interface RiskMonitoringPlan {
  id?: string;
  studyId: string;
  status: string;
  sections?: Record<string, unknown>;
  approvedBy?: string;
  approvedAt?: string;
  updatedAt?: string;
}

export async function fetchRiskMonitoringPlan(
  studyId: string,
): Promise<RiskMonitoringPlan | null> {
  const { data } = await client.get<unknown>(
    `/risk-monitoring-plans/${studyId}`,
  );
  return ((data as any)?.data ?? data) as RiskMonitoringPlan | null;
}

export async function updateRiskMonitoringPlan(
  studyId: string,
  body: Partial<RiskMonitoringPlan>,
): Promise<RiskMonitoringPlan> {
  const { data } = await client.put<RiskMonitoringPlan>(
    `/risk-monitoring-plans/${studyId}`,
    body,
  );
  return data;
}

export async function approveRiskMonitoringPlan(
  studyId: string,
): Promise<RiskMonitoringPlan> {
  const { data } = await client.post<RiskMonitoringPlan>(
    `/risk-monitoring-plans/${studyId}/approve`,
  );
  return data;
}

// ─── E-Signatures ────────────────────────────────────────────────────────────

export async function applySignature(
  body: Record<string, unknown>,
): Promise<ESignatureRecord> {
  const { data } = await client.post<ESignatureRecord>('/signatures', body);
  return data;
}

export async function fetchSignatures(
  params: Record<string, unknown>,
): Promise<PaginatedResponse<ESignatureRecord>> {
  const { data } = await client.get<unknown>(`/signatures${qs(params)}`);
  // The backend may return either a bare array or a `{ data, meta }` envelope;
  // normalise both into the flat PaginatedResponse the UI consumes.
  const raw: BackendPage<ESignatureRecord> = Array.isArray(data)
    ? { data: data as ESignatureRecord[] }
    : (data as BackendPage<ESignatureRecord>);
  return normalizePage(raw);
}

export async function fetchEntitySignatures(
  entityType: string,
  entityId: string,
): Promise<ESignatureRecord[]> {
  const { data } = await client.get<ESignatureRecord[]>(
    `/signatures/entity/${entityType}/${entityId}`,
  );
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function verifySignature(
  id: string,
): Promise<ESignatureRecord> {
  const { data } = await client.post<ESignatureRecord>(
    `/signatures/${id}/verify`,
  );
  return data;
}

export async function revokeSignature(
  id: string,
  reason: string,
): Promise<ESignatureRecord> {
  const { data } = await client.post<ESignatureRecord>(
    `/signatures/${id}/revoke`,
    { reason },
  );
  return data;
}

// ─── Safety: Cases & Regulatory Reports ──────────────────────────────────────

export async function fetchSafetyCases(
  params: Record<string, unknown>,
): Promise<PaginatedResponse<SafetyCase>> {
  const { data } = await client.get<BackendPage<SafetyCase>>(
    `/safety-cases${qs(params)}`,
  );
  return normalizePage(data);
}

export async function fetchSafetyCase(id: string): Promise<SafetyCase> {
  const { data } = await client.get<SafetyCase>(`/safety-cases/${id}`);
  return data;
}

export async function createSafetyCase(
  body: Partial<SafetyCase>,
): Promise<SafetyCase> {
  const { data } = await client.post<SafetyCase>('/safety-cases', body);
  return data;
}

export async function updateSafetyCase(
  id: string,
  body: Partial<SafetyCase>,
): Promise<SafetyCase> {
  const { data } = await client.put<SafetyCase>(`/safety-cases/${id}`, body);
  return data;
}

export async function changeSafetyCaseStatus(
  id: string,
  toStatus: string,
  comment?: string,
): Promise<SafetyCase> {
  const { data } = await client.post<SafetyCase>(
    `/safety-cases/${id}/status`,
    { toStatus, comment },
  );
  return data;
}

/**
 * Record medical review on a safety case. Backend `MedicalReviewDto` requires a
 * `notes` string. POST `/safety-cases/:id/medical-review`.
 */
export async function medicalReviewSafetyCase(
  id: string,
  body: { notes: string },
): Promise<SafetyCase> {
  const { data } = await client.post<SafetyCase>(
    `/safety-cases/${id}/medical-review`,
    body,
  );
  return data;
}

export async function createRegulatoryReport(
  caseId: string,
  body: Partial<RegulatoryReport>,
): Promise<RegulatoryReport> {
  const { data } = await client.post<RegulatoryReport>(
    `/safety-cases/${caseId}/regulatory-reports`,
    body,
  );
  return data;
}

/**
 * Update a regulatory report (e.g. mark it SUBMITTED). Backend route is keyed by
 * the REPORT id, not the case id: PUT `/regulatory-reports/:id`. `UpdateRegulatory
 * ReportDto` accepts { status, submittedTo, submittedAt, generatedXml }.
 * `caseId` is accepted for caller convenience but is not part of the route.
 */
export async function updateRegulatoryReport(
  _caseId: string,
  reportId: string,
  body: Partial<RegulatoryReport> & {
    status?: string;
    submittedTo?: string;
    submittedAt?: string;
    generatedXml?: string;
  },
): Promise<RegulatoryReport> {
  const { data } = await client.put<RegulatoryReport>(
    `/regulatory-reports/${reportId}`,
    body,
  );
  return data;
}

// ─── Payments Export ─────────────────────────────────────────────────────────

export async function previewPaymentExport(
  studyId: string,
): Promise<PaymentExportBatch> {
  const { data } = await client.get<PaymentExportBatch>(
    `/payments-export/preview${qs({ studyId })}`,
  );
  return data;
}

export async function runPaymentExport(
  body: Record<string, unknown>,
): Promise<PaymentExportBatch> {
  const { data } = await client.post<PaymentExportBatch>(
    '/payments-export',
    body,
  );
  return data;
}

export async function fetchOutstandingInvoices(studyId: string): Promise<any[]> {
  const { data } = await client.get<{ data?: unknown[] } | unknown[]>(
    `/payments-export/invoices${qs({ studyId })}`,
  );
  // Backend returns { data: invoices, totalOutstanding }; unwrap to the array.
  return Array.isArray(data) ? data : ((data as { data?: any[] })?.data ?? []);
}

// ─── Report Builder ──────────────────────────────────────────────────────────

/** A reportable entity descriptor as exposed by the report engine. */
export interface ReportEntityField {
  name: string;
  type: string;
}

export interface ReportEntity {
  entity: string;
  label: string;
  scope: 'id' | 'studyId' | null;
  fields: ReportEntityField[];
}

/** List the entities (and their fields) the report engine can query. */
export async function fetchReportEntities(): Promise<ReportEntity[]> {
  const { data } = await client.get<{ data?: ReportEntity[] } | ReportEntity[]>(
    `/report-configs/entities`,
  );
  return Array.isArray(data)
    ? data
    : ((data as { data?: ReportEntity[] })?.data ?? []);
}

export async function fetchReportConfigs(
  studyId: string,
): Promise<ReportConfigItem[]> {
  const { data } = await client.get<{ data?: ReportConfigItem[] } | ReportConfigItem[]>(
    `/report-configs${qs({ studyId })}`,
  );
  // Backend returns a paginated { data, meta }; unwrap to the array so the page
  // (which calls .filter/.length) doesn't crash.
  return Array.isArray(data)
    ? data
    : ((data as { data?: ReportConfigItem[] })?.data ?? []);
}

export async function createReportConfig(
  body: Partial<ReportConfigItem>,
): Promise<ReportConfigItem> {
  const { data } = await client.post<ReportConfigItem>('/report-configs', body);
  return data;
}

export async function updateReportConfig(
  id: string,
  body: Partial<ReportConfigItem>,
): Promise<ReportConfigItem> {
  const { data } = await client.put<ReportConfigItem>(
    `/report-configs/${id}`,
    body,
  );
  return data;
}

export async function runReportConfig(id: string): Promise<any> {
  const { data } = await client.post(`/report-configs/${id}/run`);
  return data;
}

export async function deleteReportConfig(id: string): Promise<void> {
  await client.delete(`/report-configs/${id}`);
}

// ─── Workflow Config ─────────────────────────────────────────────────────────

/**
 * Backend `GET /workflow-configs` returns a Record OBJECT keyed by workflow
 * name, e.g. `{ study: {...transitions}, site: {...}, sae: {...} }` — NOT a
 * list. Return it as-is (do not array-wrap).
 */
export async function fetchWorkflowConfigs(): Promise<Record<string, any>> {
  const { data } = await client.get<Record<string, any>>('/workflow-configs');
  return (data ?? {}) as Record<string, any>;
}

export async function fetchWorkflowConfig(
  key: string,
): Promise<WorkflowTransitions> {
  const { data } = await client.get<WorkflowTransitions>(
    `/workflow-configs/${key}`,
  );
  return data;
}

export async function updateWorkflowConfig(
  key: string,
  transitions: WorkflowTransitions,
): Promise<WorkflowTransitions> {
  const { data } = await client.put<WorkflowTransitions>(
    `/workflow-configs/${key}`,
    { transitions },
  );
  return data;
}

export async function validateTransition(
  key: string,
  from: string,
  to: string,
): Promise<{ valid: boolean }> {
  const { data } = await client.get<{ valid: boolean }>(
    `/workflow-configs/${key}/validate${qs({ from, to })}`,
  );
  return data;
}

// ─── Signing Keys (PKI enrollment for digital signatures) ────────────────────

export async function enrollSignerKey(body: {
  keyId: string;
  publicKeyJwk: JsonWebKey;
  algorithm?: string;
}): Promise<any> {
  const { data } = await client.post('/signing-keys/enroll', body);
  return data;
}

export async function fetchMySignerKeys(): Promise<any[]> {
  const { data } = await client.get<unknown[]>('/signing-keys/me');
  return Array.isArray(data) ? data : ((data as any)?.data ?? []);
}

export async function fetchCaPublicKey(): Promise<any> {
  const { data } = await client.get('/signing-keys/ca');
  return data;
}

export async function fetchSignerKey(keyId: string): Promise<any> {
  const { data } = await client.get(`/signing-keys/${keyId}`);
  return data;
}

export async function revokeSignerKey(
  keyId: string,
  reason?: string,
): Promise<any> {
  const { data } = await client.post(`/signing-keys/${keyId}/revoke`, {
    reason,
  });
  return data;
}

// ─── Remediation Phase 2/3: additional fetchers ──────────────────────────────

export async function createCountry(body: Record<string, unknown>): Promise<any> {
  const { data } = await client.post('/site-management/countries', body);
  return data;
}

export async function createMilestone(
  studyId: string,
  body: Record<string, unknown>,
): Promise<any> {
  const { data } = await client.post(`/studies/${studyId}/milestones`, body);
  return data;
}

export async function applyMilestoneTemplate(
  studyId: string,
  body: Record<string, unknown>,
): Promise<any> {
  const { data } = await client.post(
    `/studies/${studyId}/milestones/apply-template`,
    body,
  );
  return data;
}

export async function fetchAuditReadiness(studyId: string): Promise<any> {
  const { data } = await client.get(`/reporting/audit-readiness/${studyId}`);
  return data;
}

export async function fetchReportingKpis(studyId: string): Promise<any[]> {
  const { data } = await client.get(`/reporting/kpis${qs({ studyId })}`);
  return (data?.data ?? data ?? []) as any[];
}

export async function calculateReportingKpis(studyId: string): Promise<any> {
  const { data } = await client.get(`/reporting/kpis/calculate/${studyId}`);
  return data;
}

export async function fetchReportSnapshots(studyId: string): Promise<any[]> {
  const { data } = await client.get(`/reporting/snapshots${qs({ studyId })}`);
  return (data?.data ?? data ?? []) as any[];
}

export async function generateReportSnapshot(
  body: Record<string, unknown>,
): Promise<any> {
  const { data } = await client.post('/reporting/snapshots/generate', body);
  return data;
}

/**
 * GET `/reporting/enrollment/:studyId` — returns the raw enrollment report
 * object (NOT a list). Real response keys (from reporting.service
 * getEnrollmentReport):
 *   {
 *     studyId, protocolNumber, studyName,
 *     enrollmentTarget, enrollmentActual, enrollmentPercentage,
 *     enrollingSites, totalActiveSites,
 *     latestForecast: { projectedLplvDate, projectedTotal,
 *                       confidenceLow, confidenceHigh } | null,
 *     siteEnrollment: Array<{
 *       siteId, siteNumber, siteName, status,
 *       enrollmentTarget, enrollmentActual, percentage
 *     }>
 *   }
 *
 * NOTE: there is NO `enrollmentByCountry` key — the backend rolls up per-SITE
 * (`siteEnrollment`), not per-country.
 */
export async function fetchEnrollmentReport(studyId: string): Promise<any> {
  const { data } = await client.get(`/reporting/enrollment/${studyId}`);
  return data;
}
